import type { TeamMember, TeamSelection } from './team-types';

/**
 * The display rules of legacy `View/js/cart-team.js` (CTJ) and the candidate rule of
 * `View/js/cart-gcal.js` (CG), as pure functions. No React, no I/O, no host: the picker and every
 * host call the same functions, so "which cards are on screen" has exactly one answer.
 *
 * WHO is offered is NOT decided here. The member list a host passes in has already been through
 * the product, exclusion and service-area rules (for the reschedule page the server applies them);
 * these functions only order it, cut it to the display limit, pick a default and word the fee.
 *
 * Deviations from legacy, each deliberate:
 * - a member with no priority / distance sorts LAST. Legacy compared `NaN`, which is not an order
 *   at all (CTJ:185);
 * - "Show All Photographers" is a flag the host holds, not legacy's `limit="0"` attribute plus an
 *   invalid `display: 'block !important'` (CTJ:177-181).
 */

/** Legacy `nDisplayLimit` (`cart-team.asp` :15). */
export const DEFAULT_DISPLAY_LIMIT = 12;

/** Legacy `sortTeamMembers` group score (CTJ:184): favourite 3, recently used 2, everyone else 1. */
function groupScore(member: TeamMember): number {
    if (member.favorite) return 3;
    if (member.recentlyUsed) return 2;
    return 1;
}

const lastWhenMissing = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;

function compareAscending(a: number, b: number): number {
    if (a === b) return 0;
    return a < b ? -1 : 1;
}

/**
 * Legacy `sortTeamMembers` (CTJ:183-195): group score descending, then service-area priority
 * ascending, then distance ascending. Stable — members that tie keep the order they arrived in
 * (the server's legacy SQL order: own brand first, then user type, then name). Returns a new array.
 *
 * "Any Available" is not a member and is not in this list; it also scores 3 and is first in the
 * DOM, so it leads the cards (see `buildTeamCards`).
 */
export function sortMembers<T extends TeamMember>(members: readonly T[]): T[] {
    return members
        .map((member, index) => ({ member, index }))
        .sort(
            (a, b) =>
                groupScore(b.member) - groupScore(a.member) ||
                compareAscending(
                    lastWhenMissing(a.member.priority),
                    lastWhenMissing(b.member.priority)
                ) ||
                compareAscending(
                    lastWhenMissing(a.member.distance),
                    lastWhenMissing(b.member.distance)
                ) ||
                a.index - b.index
        )
        .map(entry => entry.member);
}

export interface LimitMembersOptions {
    /** Cards to show before "Show All Photographers". 0 = no limit. Default 12. */
    limit?: number;
    /** Whether the "Any Available" card is on screen. It COUNTS as one card, as legacy does. */
    hasAnyCard?: boolean;
    /** The shopper pressed "Show All Photographers". */
    isShowingAll?: boolean;
}

export interface LimitedMembers<T extends TeamMember> {
    /** The members whose cards are shown, in the order given. */
    members: T[];
    /** Whether "Show All Photographers" is offered (some members are hidden). */
    hasMore: boolean;
}

/**
 * Legacy `initMemberCount` (CTJ:38-71). The cards — "Any Available" included — are counted; when
 * `count - 1 < limit` everything shows and there is no link, otherwise the link shows and the cards
 * from index `limit` on are hidden. So: 11 photographers + Any Available, or 12 photographers
 * without it — and, because of the `- 1`, exactly 12 photographers with no Any card all show with
 * no link. `members` must already be sorted.
 */
export function limitMembers<T extends TeamMember>(
    members: readonly T[],
    options: LimitMembersOptions = {}
): LimitedMembers<T> {
    const { limit = DEFAULT_DISPLAY_LIMIT, hasAnyCard = false, isShowingAll = false } = options;
    const anyCards = hasAnyCard ? 1 : 0;
    const cardCount = members.length + anyCards;
    if (isShowingAll || limit <= 0 || cardCount - 1 < limit) {
        return { members: [...members], hasMore: false };
    }
    return { members: members.slice(0, Math.max(limit - anyCards, 0)), hasMore: true };
}

/**
 * Whether the "Any Available" card stays: the host offers it AND at least two photographers are
 * offered. Legacy hides it when exactly one other card is left (CTJ:308-312) — counted before the
 * display limit — and with nobody at all there is nothing for it to choose between.
 */
export function showsAnyCard(memberCount: number, isAnyOffered: boolean): boolean {
    return isAnyOffered && memberCount >= 2;
}

export interface BuildTeamCardsOptions {
    /** The host offers "Any Available" (the brand's team module and calendar allow it). */
    anyAvailable?: boolean;
    displayLimit?: number;
    isShowingAll?: boolean;
}

export interface TeamCards<T extends TeamMember> {
    /** Every offered member, sorted — what "Show All Photographers" would reveal. */
    all: T[];
    /** The members whose cards are on screen now, sorted. */
    members: T[];
    /** Whether the "Any Available" card is on screen. It is always the FIRST card. */
    hasAnyCard: boolean;
    /** Whether "Show All Photographers" is offered. */
    hasMore: boolean;
}

/**
 * Sort + Any Available + display limit, in legacy's order (`cartTeam.update`: sort, hide Any when
 * one card is left CTJ:308-312, then `initMemberCount` CTJ:373). The picker draws exactly this, and
 * a host calls it too when it needs to know what is on screen (the default selection; the "Any
 * Available" search, which covers only the cards displayed — CG:44-46).
 */
export function buildTeamCards<T extends TeamMember>(
    members: readonly T[],
    options: BuildTeamCardsOptions = {}
): TeamCards<T> {
    const all = sortMembers(members);
    const hasAnyCard = showsAnyCard(all.length, options.anyAvailable ?? false);
    const limited = limitMembers(all, {
        limit: options.displayLimit,
        hasAnyCard,
        isShowingAll: options.isShowingAll
    });
    return { all, members: limited.members, hasAnyCard, hasMore: limited.hasMore };
}

export interface DefaultSelectionOptions {
    /** The members whose cards are on screen, in display order (`TeamCards.members`). */
    members: readonly TeamMember[];
    /** Whether the "Any Available" card is on screen (it is the first card). */
    hasAnyCard?: boolean;
    /**
     * What is selected already — the task's current photographer on reschedule, the last choice in
     * the cart. Kept when its card is on screen.
     */
    current?: TeamSelection;
    /**
     * The "Any Available" card's own priority: a number only when the shopper's group has its own
     * service area (cart only). Absent on reschedule — which is why, there, the Any card always
     * "differs" from the first photographer in step 4.
     */
    anyPriority?: number | null;
    /**
     * Reschedule's auto-open (`reschedule.asp` :454-462): when nothing is selected by the rules
     * below, take the first card on screen rather than leaving the choice open.
     */
    fallbackToFirst?: boolean;
}

/**
 * Which card is selected when the chooser appears — legacy `cartTeam.update`, single-member mode
 * (CTJ:393-407), first match wins:
 *  1. the current selection, if its card is on screen;
 *  2. the first favourite on screen;
 *  3. the only card, when there is exactly one;
 *  4. the first card, when its priority differs from the second card's;
 *  5. nothing — the shopper must choose (no calendar until they do).
 */
export function defaultSelection(options: DefaultSelectionOptions): TeamSelection {
    const { members, hasAnyCard = false, current = null, fallbackToFirst = false } = options;

    if (current === 'any' && hasAnyCard) return 'any';
    if (typeof current === 'number' && members.some(member => member.id === current)) {
        return current;
    }

    const favourite = members.find(member => member.favorite);
    if (favourite) return favourite.id;

    // The cards in display order: Any Available first, then the photographers.
    const cards: { selection: TeamSelection; priority: number | null }[] = [
        ...(hasAnyCard
            ? [{ selection: 'any' as const, priority: options.anyPriority ?? null }]
            : []),
        ...members.map(member => ({ selection: member.id, priority: member.priority ?? null }))
    ];

    if (cards.length === 1) return cards[0].selection;
    if (cards.length > 1 && cards[0].priority !== cards[1].priority) return cards[0].selection;
    if (fallbackToFirst && cards.length > 0) return cards[0].selection;
    return null;
}

/**
 * The members a team calendar may draw on — legacy `buildCalendars` (CG:38-46): anyone in a
 * service area that is open at all. For "Any Available" pass the members whose cards are ON SCREEN
 * (legacy searches only those, CG:44-46, so the display limit narrows the search); for the hidden
 * team calendar (`cart-team.mode` 2, `cartTeam.bypass`) pass every offered member.
 */
export function teamCalendarMemberIds(members: readonly TeamMember[]): number[] {
    return members.filter(member => member.available && member.inArea).map(member => member.id);
}

/** What the "Any Available" card says about travel fees. */
export type AnyFeeRange =
    | { kind: 'hidden' }
    | { kind: 'exact'; amount: number }
    | { kind: 'upTo'; max: number }
    | { kind: 'range'; min: number; max: number };

/**
 * The "Any Available" travel-fee range — legacy CTJ:331-361 — over EVERY offered member, not just
 * the cards on screen (legacy computes it before the display limit hides anyone). A member with no
 * fee counts as 0. All zero -> hidden; all the same -> that amount; lowest is 0 -> "up to max";
 * otherwise "min to max".
 *
 * The shopper group's own flat fee (legacy `hd-trav` on member0, cart only) is
 * `TeamCartOptions.anyServiceArea.travelFee` and is not applied yet.
 */
export function anyFeeRange(members: readonly Pick<TeamMember, 'travelFee'>[]): AnyFeeRange {
    if (members.length === 0) return { kind: 'hidden' };
    const fees = members.map(member =>
        typeof member.travelFee === 'number' && Number.isFinite(member.travelFee)
            ? member.travelFee
            : 0
    );
    const min = Math.min(...fees);
    const max = Math.max(...fees);
    if (max === 0) return { kind: 'hidden' };
    if (min === max) return { kind: 'exact', amount: max };
    if (min === 0) return { kind: 'upTo', max };
    return { kind: 'range', min, max };
}

/**
 * The range in legacy's words: `$5.00`, `up to $20.00`, `$5.00 to $20.00` — the text that sits
 * between "+" and "Travel Fee" on the card. Null when nothing is shown.
 */
export function formatAnyFeeRange(
    range: AnyFeeRange,
    formatMoney: (amount: number) => string,
    /** Translates the two connecting words ("up to", "to"). Identity by default. */
    translate: (text: string) => string = text => text
): string | null {
    switch (range.kind) {
        case 'exact':
            return formatMoney(range.amount);
        case 'upTo':
            return `${translate('up to')} ${formatMoney(range.max)}`;
        case 'range':
            return `${formatMoney(range.min)} ${translate('to')} ${formatMoney(range.max)}`;
        default:
            return null;
    }
}
